import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertTriangle, RefreshCw, ExternalLink, Wallet, Info, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useInterval } from '@/hooks/use-interval';
import { useAuthRefresh } from '@/hooks/useAuthRefresh';
import FinancialOverview from './finances/FinancialOverview';
import { ErrorRecovery } from './finances/ErrorRecovery';

interface FinancesManagerProps {
  userId: string;
}

type PayoutStatus = 'pending' | 'in_progress' | 'active' | 'restricted' | 'rejected';
type SubscriptionStatus = 'inactive' | 'trialing' | 'active' | 'past_due' | 'canceled';

interface PractitionerFinanceDetails {
  payout_details_status: PayoutStatus;
  stripe_account_id: string | null;
  subscription_status: SubscriptionStatus;
}

interface ClinicalEligibility {
  can_list: boolean;
  license_status: string | null;
  subscription_status: string | null;
  listed_service_count: number;
  unlisted_publishable_service_count: number;
  missing_reasons: string[];
}

interface ServiceTrackSummary {
  hasClinical: boolean;
  hasWellness: boolean;
}

const isMobileDevice = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  window.innerWidth <= 768;

const FinancesManager: React.FC<FinancesManagerProps> = ({ userId }) => {
  const { t } = useTranslation('finances');
  const [wellnessLoading, setWellnessLoading] = useState(false);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [financeDetails, setFinanceDetails] = useState<PractitionerFinanceDetails | null>(null);
  const [clinicalEligibility, setClinicalEligibility] = useState<ClinicalEligibility | null>(null);
  const [trackSummary, setTrackSummary] = useState<ServiceTrackSummary>({ hasClinical: false, hasWellness: false });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [pollingReason, setPollingReason] = useState<'wellness' | 'clinical' | null>(null);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [forceRefresh, setForceRefresh] = useState(false);
  const { refreshSession } = useAuthRefresh();
  const realtimeChannelRef = useRef<any>(null);
  const smsSentRef = useRef(false);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onboardingParam = searchParams.get('onboarding');
  const stripeParam = searchParams.get('stripe');
  const hasOnboardingParam = onboardingParam === 'refresh' || onboardingParam === 'success';
  // Fix #2 / Fix #4: detect Stripe Checkout return via ?stripe=success
  const hasStripeSuccess = stripeParam === 'success';

  const getStatusMessages = () => ({
    pending: {
      title: t('status.notConnected.title'),
      description: t('status.notConnected.description'),
      icon: <Info className="h-6 w-6 text-blue-500" />,
      color: 'border-blue-200 bg-blue-50'
    },
    in_progress: {
      title: t('status.inProgress.title'),
      description: t('status.inProgress.description'),
      icon: <RefreshCw className="h-6 w-6 text-amber-500" />,
      color: 'border-amber-200 bg-amber-50'
    },
    active: {
      title: t('status.active.title'),
      description: t('status.active.description'),
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      color: 'border-green-200 bg-green-50'
    },
    restricted: {
      title: t('status.restricted.title'),
      description: t('status.restricted.description'),
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      color: 'border-red-200 bg-red-50'
    },
    rejected: {
      title: t('status.rejected.title'),
      description: t('status.rejected.description'),
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      color: 'border-red-200 bg-red-50'
    }
  });

  // ─── Fix #1: fetch get_clinical_eligibility() as single source of truth ───
  // background=true → skip loading spinner (used by polling and realtime listener)
  const fetchFinanceDetails = useCallback(async (showToast = false, background = false) => {
    try {
      if (!background) setIsLoadingData(true);
      setLastError(null);

      // Real-time Stripe Connect status check
      try {
        const { data: stripeCheck, error: stripeError } = await supabase.functions.invoke('check-stripe-status');
        if (stripeCheck && !stripeError && stripeCheck.updated && showToast) {
          toast({ title: t('toast.statusUpdated'), description: t('toast.statusUpdatedDescription') });
        }
      } catch {
        // non-fatal
      }

      // Fetch finance/payout fields
      const { data, error } = await supabase
        .from('practitioners')
        .select('payout_details_status, stripe_account_id, subscription_status')
        .eq('id', userId)
        .single();

      let latestPayoutStatus: string | null = null;
      if (error) {
        setLastError(t('errors.failedToLoad'));
        toast({ variant: 'destructive', title: t('errors.connectionError'), description: t('errors.failedToLoad') });
      } else {
        const details = {
          payout_details_status: data.payout_details_status as PayoutStatus,
          stripe_account_id: data.stripe_account_id,
          subscription_status: (data.subscription_status as SubscriptionStatus) || 'inactive',
        };
        setFinanceDetails(details);
        latestPayoutStatus = details.payout_details_status;

        if (showToast) {
          toast({ title: t('toast.statusUpdated'), description: t('toast.statusUpdatedDescription') });
        }
      }

      // Fix #1: call get_clinical_eligibility() — single source of truth for can_list
      let latestCanList: boolean | null = null;
      try {
        const { data: eligData, error: eligError } = await supabase.rpc('get_clinical_eligibility');
        if (!eligError && eligData && typeof eligData === 'object' && !Array.isArray(eligData)) {
          const e = eligData as Record<string, any>;
          const eligibility: ClinicalEligibility = {
            can_list: e.can_list ?? false,
            license_status: e.license_status ?? null,
            subscription_status: e.subscription_status ?? null,
            listed_service_count: e.listed_service_count ?? 0,
            unlisted_publishable_service_count: e.unlisted_publishable_service_count ?? 0,
            missing_reasons: Array.isArray(e.missing_reasons) ? e.missing_reasons : [],
          };
          setClinicalEligibility(eligibility);
          latestCanList = eligibility.can_list;
          if (background) {
            console.log(`[polling] can_list = ${latestCanList}, subscription_status = ${eligibility.subscription_status}`);
          }
        }
      } catch {
        // If practitioner has no clinical services or is wellness-only, silently skip
      }

      // ─── Inline stop condition check (uses fresh local data, no stale closure) ───
      if (background) {
        const shouldStop = latestPayoutStatus === 'active' || latestCanList === true;
        if (shouldStop) {
          setForceRefresh(false);
          setPollingReason(null);
          if (latestCanList === true) {
            toast({ title: 'Clinical Track Active', description: 'Your clinical services are now listed.' });
          } else if (latestPayoutStatus === 'active') {
            toast({ title: t('auth.stripeSetupComplete'), description: t('auth.stripeSetupCompleteDescription') });
          }
        }
      }

      // Service track summary
      const { data: services } = await supabase
        .from('services')
        .select('service_track')
        .eq('practitioner_id', userId)
        .eq('is_active', true);

      setTrackSummary({
        hasClinical: (services || []).some((s: any) => s.service_track === 'clinical'),
        hasWellness: (services || []).some((s: any) => s.service_track === 'wellness' || !s.service_track),
      });
    } catch (err) {
      setLastError(t('errors.unexpectedError'));
    } finally {
      if (!background) setIsLoadingData(false);
      setRefreshingStatus(false);
    }
  }, [userId, t, toast]);

  // Initial load
  useEffect(() => {
    fetchFinanceDetails();
  }, [fetchFinanceDetails]);

  // Fix #2: Realtime listener — refetch whenever the practitioners row updates
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`practitioner-finance-${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'practitioners', filter: `id=eq.${userId}` },
        () => {
          fetchFinanceDetails(false, true); // background=true: no loading spinner
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, fetchFinanceDetails]);

  // Fix #2 & #3: detect ?stripe=success → verify/refresh session, then wait 3s for webhook lag before fetching
  useEffect(() => {
    if (!hasStripeSuccess) return;

    setPollingReason('clinical');
    setForceRefresh(true);

    // Clean up URL param immediately
    const newUrl = window.location.pathname + window.location.search.replace(/[?&]stripe=[^&]+/, '').replace(/^\?$/, '');
    window.history.replaceState({}, '', newUrl || window.location.pathname);

    const initAfterStripeReturn = async () => {
      // Fix #3: Verify session is still valid after Stripe redirect
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('[FinancesManager] Session lost after Stripe redirect — attempting refresh');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('[FinancesManager] Session refresh failed after Stripe redirect:', refreshError);
          toast({ variant: 'destructive', title: t('auth.sessionExpired'), description: t('auth.pleaseReLoginAndRetry') });
          return;
        }
        console.log('[FinancesManager] Session refreshed successfully after Stripe redirect');
      }

      // Fix #2: Wait 3s for Stripe webhook to update subscription_status before first fetch
      await new Promise(resolve => setTimeout(resolve, 3000));
      fetchFinanceDetails(true);
    };

    initAfterStripeReturn();
  }, [hasStripeSuccess]);

  // SMS trigger: fire once after hasStripeSuccess + financeDetails available
  // useRef as primary guard (synchronous), sessionStorage as backup across page refreshes
  useEffect(() => {
    if (!hasStripeSuccess) return;
    if (smsSentRef.current) return;
    if (sessionStorage.getItem('clinical_sms_sent')) return;
    if (!financeDetails) return;

    smsSentRef.current = true;
    sessionStorage.setItem('clinical_sms_sent', 'true');

    supabase.functions.invoke('send-clinical-subscription-sms').catch((err) => {
      console.warn('[FinancesManager] Clinical SMS failed (non-fatal):', err);
    });
  }, [hasStripeSuccess, financeDetails]);

  // Stripe Connect (Wellness) onboarding param handling
  useEffect(() => {
    if (hasOnboardingParam) {
      setPollingReason('wellness');
      setForceRefresh(true);
      fetchFinanceDetails(true);
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]onboarding=[^&]+/, '');
      window.history.replaceState({}, '', newUrl);
      if (onboardingParam === 'success') {
        toast({ title: t('toast.stripeSetupInitiated'), description: t('toast.stripeSetupInitiatedDescription') });
      }
    }
  }, [hasOnboardingParam, location]);

  // Polling when forceRefresh is active — background=true skips loading spinner
  useInterval(() => {
    if (forceRefresh) fetchFinanceDetails(false, true);
  }, forceRefresh ? 5000 : null);

  // Safety timeout: stop polling after 2 minutes no matter what.
  // Uses useRef so the timeout ID is stable and can be cancelled reliably.
  useEffect(() => {
    if (!forceRefresh) {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }
    // Set timeout once when forceRefresh becomes true
    pollingTimeoutRef.current = setTimeout(() => {
      console.log('[polling] 2-minute timeout reached — force-stopping polling');
      setForceRefresh(false);
      setPollingReason(null);
      fetchFinanceDetails(false, false);
    }, 120000);
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [forceRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectOrContinueStripe = async () => {
    setWellnessLoading(true);
    setLastError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ variant: 'destructive', title: t('auth.sessionExpired'), description: t('auth.pleaseReLoginAndRetry') });
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-connect-account', { method: 'POST' });
      if (error) {
        if (error.message?.includes('Authentication') || error.message?.includes('session')) {
          const refreshed = await refreshSession();
          if (refreshed) {
            const { data: retryData, error: retryError } = await supabase.functions.invoke('create-connect-account', { method: 'POST' });
            if (retryError) { setLastError(retryError.message || t('toast.connectionFailedDescription')); toast({ variant: 'destructive', title: t('toast.connectionFailed'), description: retryError.message }); return; }
            if (retryData?.onboardingUrl) { processStripeUrl(retryData.onboardingUrl); return; }
          } else { toast({ variant: 'destructive', title: t('auth.authFailed'), description: t('auth.pleaseReLoginAndRetry') }); return; }
        }
        setLastError(error.message || t('toast.connectionFailedDescription'));
        toast({ variant: 'destructive', title: t('toast.connectionFailed'), description: error.message });
        return;
      }
      if (data?.onboardingUrl) { processStripeUrl(data.onboardingUrl); }
      else { setLastError(t('toast.invalidResponse')); toast({ variant: 'destructive', title: t('toast.connectionFailed'), description: t('toast.invalidResponse') }); }
    } catch (err: any) {
      setLastError(err.message); toast({ variant: 'destructive', title: t('toast.connectionFailed'), description: err.message });
    } finally { setWellnessLoading(false); }
  };

  const processStripeUrl = (url: string) => {
    if (isMobileDevice()) {
      toast({ title: t('toast.stripeSetup'), description: t('auth.mobileRedirectDescription'), duration: 5000 });
      window.location.href = url;
    } else {
      window.open(url, '_blank');
      toast({ title: t('toast.stripeSetup'), description: t('toast.stripeSetupDescription', { action: financeDetails?.stripe_account_id ? t('toast.continueYourSetup') : t('toast.completeSetup') }) });
    }
    setPollingReason('wellness');
    setForceRefresh(true);
  };

  const handleRefreshStatus = () => { setRefreshingStatus(true); fetchFinanceDetails(true); };

  const handleStartSubscription = async () => {
    setClinicalLoading(true);
    setLastError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-practitioner-subscription-session', { method: 'POST' });
      if (error) throw error;
      const checkoutUrl = (data as any)?.url;
      if (!checkoutUrl) throw new Error('Subscription checkout URL not found.');
      window.open(checkoutUrl, '_blank');
      // Start polling immediately — DB trigger will fire when webhook updates subscription_status
      setPollingReason('clinical');
      setForceRefresh(true);
    } catch (err: any) {
      const message = err?.message || t('toast.unexpectedError');
      setLastError(message);
      toast({ variant: 'destructive', title: t('subscription.setupFailed'), description: message });
    } finally { setClinicalLoading(false); }
  };

  const handleManageSubscription = async () => {
    setClinicalLoading(true);
    setLastError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-practitioner-subscription-portal', { method: 'POST' });
      if (error) throw error;
      const portalUrl = (data as any)?.url;
      if (!portalUrl) throw new Error('Subscription portal URL not found.');
      window.open(portalUrl, '_blank');
    } catch (err: any) {
      const message = err?.message || t('toast.unexpectedError');
      setLastError(message);
      toast({ variant: 'destructive', title: t('subscription.portalFailed'), description: message });
    } finally { setClinicalLoading(false); }
  };

  if (isLoadingData) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const statusMeta: Record<SubscriptionStatus, { title: string; description: string; tone: string }> = {
    inactive: { title: t('subscription.inactive.title'), description: t('subscription.inactive.description'), tone: 'border-amber-200 bg-amber-50' },
    trialing: { title: t('subscription.trialing.title'), description: t('subscription.trialing.description'), tone: 'border-blue-200 bg-blue-50' },
    active: { title: t('subscription.active.title'), description: t('subscription.active.description'), tone: 'border-green-200 bg-green-50' },
    past_due: { title: t('subscription.pastDue.title'), description: t('subscription.pastDue.description'), tone: 'border-red-200 bg-red-50' },
    canceled: { title: t('subscription.canceled.title'), description: t('subscription.canceled.description'), tone: 'border-slate-200 bg-slate-50' },
  };

  const subStatus = financeDetails?.subscription_status || 'inactive';
  const subDisplay = statusMeta[subStatus];
  const canManageSub = subStatus === 'active' || subStatus === 'trialing' || subStatus === 'past_due';

  const getDisplayStatus = () => {
    if (pollingReason === 'wellness') return { status: 'in_progress', title: t('status.processing.title'), description: t('status.processing.description'), icon: <RefreshCw className="h-6 w-6 animate-spin text-amber-500" />, color: 'border-amber-200 bg-amber-50' };
    const status = financeDetails?.payout_details_status || 'pending';
    return getStatusMessages()[status];
  };

  const getButtonText = () => {
    if (financeDetails?.payout_details_status === 'active') return t('buttons.manageStripe');
    if (financeDetails?.stripe_account_id) return t('buttons.continueSetup');
    return t('buttons.connectStripe');
  };

  const displayStatus = getDisplayStatus();
  const buttonText = getButtonText();
  const isStripeActive = financeDetails?.payout_details_status === 'active';

  // Fix #1: eligibility banner computed from get_clinical_eligibility (can_list is single truth)
  const renderClinicalEligibilityBanner = () => {
    if (!trackSummary.hasClinical && !clinicalEligibility) return null;

    if (clinicalEligibility?.can_list) {
      return (
        <Alert className="border-green-300 bg-green-50 mt-4">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900 font-semibold">Clinical Services Listed</AlertTitle>
          <AlertDescription className="text-green-800">
            {clinicalEligibility.listed_service_count > 0
              ? `${clinicalEligibility.listed_service_count} service(s) are publicly listed.${clinicalEligibility.unlisted_publishable_service_count > 0 ? ` ${clinicalEligibility.unlisted_publishable_service_count} more ready to list.` : ''}`
              : 'You are eligible. Complete a clinical service to go live.'}
          </AlertDescription>
        </Alert>
      );
    }

    const reasons = clinicalEligibility?.missing_reasons ?? [];
    const hasLicenseIssue = reasons.includes('license_not_approved');
    const hasSubIssue = reasons.includes('subscription_inactive');

    if (hasLicenseIssue && !hasSubIssue) {
      return (
        <Alert className="border-amber-300 bg-amber-50 mt-4">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900 font-semibold">Subscription Active — Awaiting License Approval</AlertTitle>
          <AlertDescription className="text-amber-800">
            Submit your professional license for review to go live on the Clinical Track.
          </AlertDescription>
        </Alert>
      );
    }

    if (hasSubIssue && !hasLicenseIssue) {
      return (
        <Alert className="border-amber-300 bg-amber-50 mt-4">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900 font-semibold">License Approved — Subscribe to Go Live</AlertTitle>
          <AlertDescription className="text-amber-800">
            Your license is verified. Start a Clinical Track subscription to list your services.
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6 pb-8 md:pb-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefreshStatus} disabled={refreshingStatus} className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3">
          {refreshingStatus ? (
            <><Loader2 className="h-4 w-4 animate-spin sm:mr-2" /><span className="hidden sm:inline">{t('refreshing')}</span></>
          ) : (
            <><RefreshCw className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{t('refreshStatus')}</span></>
          )}
        </Button>
      </div>

      {lastError && (
        <ErrorRecovery error={lastError} onRetry={() => { setLastError(null); fetchFinanceDetails(true); }} onRefreshSession={refreshSession} />
      )}

      {/* Track Comparison Overview */}
      <Card className="border border-border bg-muted/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            <Info className="mr-2 h-5 w-5 text-primary" />
            {t('trackComparison.title')}
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {t('trackComparison.description')}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Side-by-Side Track Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Wellness Track Card */}
        <Card className="border-2 border-emerald-200 flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-emerald-700">
                <Wallet className="mr-2 h-5 w-5" />
                {t('wellnessTrack.title')}
              </CardTitle>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                {t('wellnessTrack.default')}
              </span>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start"><span className="mr-2 text-emerald-500">•</span>{t('wellnessTrack.features.billing')}</li>
              <li className="flex items-start"><span className="mr-2 text-emerald-500">•</span>{t('wellnessTrack.features.matching')}</li>
              <li className="flex items-start"><span className="mr-2 text-emerald-500">•</span>{t('wellnessTrack.features.revenueShare')}</li>
              <li className="flex items-start"><span className="mr-2 text-emerald-500">•</span>{t('wellnessTrack.features.noLicense')}</li>
              <li className="flex items-start"><span className="mr-2 text-emerald-500">•</span>{t('wellnessTrack.features.stripeConnect')}</li>
            </ul>
          </CardHeader>
          <CardContent className="flex-1">
            {financeDetails?.payout_details_status === 'pending' && (
              <Alert className="border-blue-500 bg-blue-50 mb-4">
                <AlertCircle className="h-5 w-5 text-blue-600" />
                <AlertTitle className="text-blue-900 font-semibold">{t('platformMigration.title')}</AlertTitle>
                <AlertDescription className="text-blue-800">{t('platformMigration.description')}</AlertDescription>
              </Alert>
            )}
            <div className={`rounded-lg p-4 ${displayStatus.color}`}>
              <div className="flex items-center space-x-4">
                {displayStatus.icon}
                <div>
                  <h3 className="text-sm font-semibold">{displayStatus.title}</h3>
                  <p className="text-sm text-muted-foreground">{displayStatus.description}</p>
                  {isMobileDevice() && financeDetails?.payout_details_status !== 'active' && (
                    <p className="text-xs text-amber-600 mt-2">📱 {t('auth.mobileRedirectDescription')}</p>
                  )}
                </div>
              </div>
            </div>
            {financeDetails?.stripe_account_id && financeDetails.payout_details_status === 'in_progress' && (
              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertTitle>{t('connection.setupRequired.title')}</AlertTitle>
                <AlertDescription>{t('connection.setupRequired.description')}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter>
            <Button onClick={handleConnectOrContinueStripe} disabled={wellnessLoading || pollingReason === 'wellness'} className="w-full md:w-auto">
              {wellnessLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('buttons.processing')}</>)
                : pollingReason === 'wellness' ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('buttons.updatingStatus')}</>)
                : (<><ExternalLink className="mr-2 h-4 w-4" />{buttonText}</>)}
            </Button>
          </CardFooter>
        </Card>

        {/* Clinical Track Card */}
        <Card className="border-2 border-indigo-200 flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center text-indigo-700">
              <Wallet className="mr-2 h-5 w-5" />
              {t('clinicalTrack.title')}
            </CardTitle>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start"><span className="mr-2 text-indigo-500">•</span>{t('clinicalTrack.features.billing')}</li>
              <li className="flex items-start"><span className="mr-2 text-indigo-500">•</span>{t('clinicalTrack.features.matching')}</li>
              <li className="flex items-start"><span className="mr-2 text-indigo-500">•</span>{t('clinicalTrack.features.revenueShare')}</li>
              <li className="flex items-start"><span className="mr-2 text-indigo-500">•</span>{t('clinicalTrack.features.aiTools')}</li>
              <li className="flex items-start"><span className="mr-2 text-indigo-500">•</span>{t('clinicalTrack.features.license')}</li>
            </ul>
          </CardHeader>
          <CardContent className="flex-1">
            {pollingReason === 'clinical' && (
              <div className="rounded-lg p-4 border border-indigo-200 bg-indigo-50 mb-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold">Activating subscription…</h3>
                    <p className="text-sm text-muted-foreground">Verifying your payment with Stripe. This may take a moment.</p>
                    {clinicalEligibility?.subscription_status && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current status: <span className="font-medium capitalize">{clinicalEligibility.subscription_status}</span>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (pollingTimeoutRef.current) {
                      clearTimeout(pollingTimeoutRef.current);
                      pollingTimeoutRef.current = null;
                    }
                    setPollingReason(null);
                    setForceRefresh(false);
                  }}
                  className="mt-2 text-xs text-indigo-600 underline hover:text-indigo-800 cursor-pointer bg-transparent border-none p-0"
                >
                  Dismiss
                </button>
              </div>
            )}
            <div className={`rounded-lg p-4 ${subDisplay.tone}`}>
              <h3 className="text-sm font-semibold">{subDisplay.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{subDisplay.description}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-3 italic">{t('clinicalTrack.requirement')}</p>
            {!canManageSub && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground">
                  First-time subscribers get a 30-day free trial automatically. You can apply a promo code on the next page.
                </p>
              </div>
            )}
            {/* Fix #1: Eligibility banner driven by get_clinical_eligibility */}
            {renderClinicalEligibilityBanner()}
          </CardContent>
          <CardFooter>
            <Button onClick={canManageSub ? handleManageSubscription : handleStartSubscription} disabled={clinicalLoading || pollingReason === 'clinical'} className="w-full md:w-auto">
              {clinicalLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('subscription.processing')}</>)
                : pollingReason === 'clinical' ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Activating…</>)
                : canManageSub ? (<><ExternalLink className="mr-2 h-4 w-4" />{t('subscription.manageSubscription')}</>)
                : (<><ExternalLink className="mr-2 h-4 w-4" />{t('subscription.startSubscription')}</>)}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Financial Overview (shown when Stripe Connect is active) */}
      {isStripeActive && financeDetails?.stripe_account_id && (
        <FinancialOverview stripeAccountId={financeDetails.stripe_account_id} />
      )}
    </div>
  );
};

export default FinancesManager;
